import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { getBestMatchingPlan, getPlanNameFromQuota } from '../../utils/planMapping';
import quotaService from '../../services/quotaService';
import PurchaseCreditsDisplay from './PurchaseCreditsDisplay';

const QuotaDisplay = ({ userId, planName = "Rookie" }) => {
  const { 
    currentQuota, 
    quotaLoading, 
    loadUserQuota, 
    currentSubscription, 
    plans, 
    loadUserSubscription,
    subscriptionRefreshTrigger 
  } = useSubscription();
  const navigate = useNavigate();

  // Determine plan name using the new plan mapping utility
  const actualPlanName = React.useMemo(() => {
    // If planName prop is provided and not default, use it
    if (planName && planName !== "Rookie") {
      return planName;
    }
    
    // Use the new plan mapping utility to get the best matching plan
    const bestPlan = getBestMatchingPlan(currentSubscription, currentQuota, plans);
    if (bestPlan) {
      return bestPlan.name;
    }
    
    // Final fallback to quota-based inference
    if (currentQuota && currentQuota.limitSeconds) {
      const inferredPlanName = getPlanNameFromQuota(currentQuota.limitSeconds);
      return inferredPlanName;
    }
    
    return planName || "Unknown Plan";
  }, [planName, currentSubscription, plans, currentQuota]);

  // Load quota data when component mounts and when userId changes
  React.useEffect(() => {
    if (userId) {
      loadUserQuota().catch(console.error);
      // Also try to load subscription if it's null
      if (!currentSubscription) {
        loadUserSubscription().catch(console.error);
      }
    }
  }, [userId]); // Remove function dependencies to prevent infinite loops

  // Respond to subscription refresh triggers
  React.useEffect(() => {
    if (subscriptionRefreshTrigger && userId) {
      console.log('🔄 QuotaDisplay: Responding to subscription refresh trigger');
      // These are async functions, but we don't need to await them in useEffect
      loadUserQuota().catch(console.error);
      loadUserSubscription().catch(console.error);
    }
  }, [subscriptionRefreshTrigger, userId]);

  // Set up periodic refresh for real-time updates
  React.useEffect(() => {
    if (!userId) return;

    // Refresh quota every 30 seconds for real-time updates
    const intervalId = setInterval(() => {
      loadUserQuota().catch(console.error);
    }, 30000);

    return () => clearInterval(intervalId);
  }, [userId]); // Remove loadUserQuota from dependencies

  if (quotaLoading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!currentQuota) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-500 text-sm">No quota information available</p>
      </div>
    );
  }

  const { limitSeconds, usedSeconds, remainingSeconds, overLimit } = currentQuota;
  
  // Calculate usage percentage
  const usagePercentage = quotaService.calculateUsagePercentage(usedSeconds, limitSeconds);
  const storageStatus = quotaService.getStorageStatus(usedSeconds, limitSeconds);
  const isOverLimit = quotaService.isOverLimit(usedSeconds, limitSeconds);

  // Get status colors
  const getStatusColors = () => {
    switch (storageStatus) {
      case 'critical':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          progress: 'bg-red-600',
          border: 'border-red-200'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          progress: 'bg-yellow-600',
          border: 'border-yellow-200'
        };
      case 'safe':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          progress: 'bg-green-600',
          border: 'border-green-200'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          progress: 'bg-gray-600',
          border: 'border-gray-200'
        };
    }
  };

  const colors = getStatusColors();

  return (
    <div className="space-y-3">
      {/* Storage Quota */}
      <div className={`p-3 ${colors.bg} rounded-lg ${colors.border} border`}>
        {/* Compact horizontal layout */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-semibold text-gray-800">Storage Used (Plan: {actualPlanName})</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.text}`}>
              {storageStatus.toUpperCase()}
            </span>
            <button
              onClick={() => {
                loadUserQuota().catch(console.error);
                loadUserSubscription().catch(console.error);
              }}
              disabled={quotaLoading}
              className="ml-2 p-1 text-gray-500 hover:text-gray-700 transition-colors"
              title="Refresh quota and subscription data"
            >
              <svg className={`w-4 h-4 ${quotaLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-800">
              {quotaService.formatStorage(usedSeconds)} / {quotaService.formatStorage(limitSeconds)}
            </div>
            <div className="text-xs text-gray-600">{usagePercentage}%</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div 
            className={`h-2 rounded-full ${colors.progress} transition-all duration-300`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>

        {/* Over Limit Warning - Compact */}
        {isOverLimit && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-4 h-4 text-red-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 text-xs font-medium">
                Storage limit exceeded. Upgrade to avoid auto-deletion in 24 hours.
              </span>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-brand-red text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors"
            >
              Browse Plans
            </button>
          </div>
        )}
      </div>

      {/* Purchase Credits Display */}
      <PurchaseCreditsDisplay userId={userId} />
    </div>
  );
};

export default QuotaDisplay;
